/**
 * TrustCommerce Return Bridge Page
 * 
 * This page handles the TC Premier popup return flow:
 * 1. Receives TC callback parameters from URL
 * 2. Calls backend to finalize the card save
 * 3. Notifies opener via postMessage
 * 4. Closes the popup automatically
 * 
 * This runs in the POPUP window, not the parent.
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { SpinnerGap, CheckCircle, XCircle } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TrustCommerceReturnPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing"); // processing | success | error
  const [message, setMessage] = useState("Processing your card...");
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Parse TC callback parameters from URL
        // TC returns: transaction[billingId], transaction[status_name], etc.
        const params = {};
        for (const [key, value] of searchParams.entries()) {
          params[key] = value;
        }

        console.log("[TC Bridge] Received params:", Object.keys(params));

        // Extract transaction fields from bracket notation
        const transaction = {};
        for (const [key, value] of Object.entries(params)) {
          const match = key.match(/^transaction\[(\w+)\]$/);
          if (match) {
            transaction[match[1]] = value;
          }
        }

        // Also check for flat params (fallback)
        const billingId = transaction.billingId || params.billingId || params.billingid;
        const statusName = transaction.status_name || params.status_name || params.status;
        const actionName = transaction.action_name || params.action_name || params.action;
        const transid = transaction.transid || params.transid;
        const sessionToken = params.session_token;

        console.log("[TC Bridge] Extracted:", {
          billingId: billingId ? billingId.substring(0, 4) + "..." : null,
          statusName,
          actionName,
          transid,
          hasSession: !!sessionToken
        });

        // Validate basic success criteria client-side first
        const isSuccess = 
          statusName?.toLowerCase() === "accepted" &&
          actionName?.toLowerCase() === "store" &&
          billingId &&
          transid;

        if (!isSuccess) {
          throw new Error(
            statusName?.toLowerCase() === "declined" 
              ? "Card was declined. Please try a different card."
              : "Card could not be saved. Please try again."
          );
        }

        // Call backend to finalize the card save
        // The backend will validate, save, and set as default
        const queryString = new URLSearchParams(params).toString();
        const response = await fetch(`${API}/billing/trustcommerce/finalize?${queryString}`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json"
          }
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.error || result.detail || "Failed to save payment method");
        }

        // SUCCESS - notify opener and close
        setStatus("success");
        setMessage("Card added successfully!");

        // Send success message to parent window
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            { 
              type: "TC_CARD_SAVED", 
              ok: true,
              paymentMethodId: result.payment_method_id 
            },
            window.location.origin
          );
          console.log("[TC Bridge] Posted success message to opener");
        }

        // Auto-close after brief delay
        setTimeout(() => {
          try {
            window.close();
          } catch (e) {
            // If close is blocked, show manual close button
            setCanClose(true);
          }
        }, 1500);

      } catch (error) {
        console.error("[TC Bridge] Error:", error);
        setStatus("error");
        setMessage(error.message || "An error occurred. Please try again.");
        setCanClose(true);

        // Send failure message to parent window
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            { 
              type: "TC_CARD_SAVED", 
              ok: false, 
              error: error.message 
            },
            window.location.origin
          );
          console.log("[TC Bridge] Posted error message to opener");
        }
      }
    };

    processCallback();
  }, [searchParams]);

  const handleManualClose = () => {
    try {
      window.close();
    } catch (e) {
      // Fallback: tell user to close manually
      setMessage("Please close this window manually.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center">
          {status === "processing" && (
            <>
              <SpinnerGap className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
              <h2 className="text-lg font-semibold text-gray-900">{message}</h2>
              <p className="text-sm text-gray-500 mt-2">Please wait...</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-600" weight="fill" />
              <h2 className="text-lg font-semibold text-gray-900">{message}</h2>
              <p className="text-sm text-gray-500 mt-2">This window will close automatically.</p>
              {canClose && (
                <Button onClick={handleManualClose} className="mt-4">
                  Close Window
                </Button>
              )}
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="w-12 h-12 mx-auto mb-4 text-red-600" weight="fill" />
              <h2 className="text-lg font-semibold text-gray-900">Unable to Save Card</h2>
              <p className="text-sm text-gray-600 mt-2">{message}</p>
              <Button onClick={handleManualClose} className="mt-4">
                Close Window
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
